import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import './Footer.css';

const Footer = () => {
  const [newsletterStatus, setNewsletterStatus] = useState(null);
  const [newsletterMessage, setNewsletterMessage] = useState('');

  const handleNewsletterSubmit = (e) => {
    e.preventDefault();
    const email = e.target.EMAIL.value;
    if (!email) return;

    setNewsletterStatus('sending');
    setNewsletterMessage('');

    // Remove old script and handler if they exist from a previous run
    const cleanup = () => {
      delete window.handleMailchimpResponse;
      const script = document.getElementById('mc-jsonp');
      if (script) script.remove();
    };
    cleanup();

    let callbackExecuted = false;

    const url = `https://timmers-bv.us10.list-manage.com/subscribe/post-json?u=4e1ead804e072658f96c27429&id=0f0992dddb&f_id=00cc4ae4f0&EMAIL=${encodeURIComponent(email)}&c=handleMailchimpResponse`;

    // We use a timeout as a fallback for pure network failures.
    const timeoutId = setTimeout(() => {
      if (!callbackExecuted) {
        setNewsletterStatus('error');
        setNewsletterMessage('Er kon geen verbinding worden gemaakt met Mailchimp. Probeer het later opnieuw.');
        cleanup();
      }
    }, 10000);

    window.handleMailchimpResponse = (data) => {
      callbackExecuted = true;
      clearTimeout(timeoutId);
      if (data.result === 'success') {
        setNewsletterStatus('success');
        setNewsletterMessage('Je bent succesvol aangemeld voor onze nieuwsbrief!');
      } else {
        setNewsletterStatus('error');
        if (data.msg && data.msg.includes('already subscribed')) {
          setNewsletterMessage('Dit e-mailadres is al geabonneerd.');
        } else {
          // Clean up HTML tags Mailchimp sometimes sends in messages
          const cleanMsg = data.msg ? data.msg.replace(/(<([^>]+)>)/gi, "") : 'Oops! Er is iets misgegaan bij het verzenden.';
          setNewsletterMessage(cleanMsg);
        }
      }
      cleanup();
    };

    const script = document.createElement('script');
    script.id = 'mc-jsonp';
    script.src = url;

    script.onload = () => {
      // If onload fires but our callback wasn't executed, Mailchimp sent invalid JSONP (the double callback bug).
      if (!callbackExecuted) {
        clearTimeout(timeoutId);
        setNewsletterStatus('error');
        setNewsletterMessage('Dit e-mailadres kan niet worden toegevoegd of is al geabonneerd.');
        cleanup();
      }
    };

    script.onerror = () => {
      clearTimeout(timeoutId);
      if (!callbackExecuted) {
        setNewsletterStatus('error');
        setNewsletterMessage('Er kon geen verbinding worden gemaakt met Mailchimp.');
      }
      cleanup();
    };

    document.body.appendChild(script);
  };

  return (
    <footer id="footer">
      <section className="footer">
        <div>
          <div className="container">
            <div className="footer-13-main">
              <div className="footer-13-company">
                <div className="column-large-2 column-left">
                  <Link to="/" className="footer-13-logo-link w-inline-block">
                    <img
                      loading="lazy"
                      src="/media/logo/websites/1/logo-large.png"
                      alt="Timmers BV Logo"
                      className="logo"
                    />
                  </Link>
                  <div className="paragraph">
                    Wij zijn een groothandel in glas, glasbeslag en beglazingsmaterialen
                    gevestigd in Oosterhout (NB). Specialist in glastoepassingen gecombineerd met glasbeslag en
                    de toelevering van alle verwerkingsmaterialen voor de glaswereld.
                  </div>
                </div>
                <div className="row-x-small-2">
                  <a
                    href="https://www.instagram.com/timmersbv/"
                    className="icon-button-outline-large w-inline-block"
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label="Volg ons op Instagram"
                  >
                    <div className="icon-regular w-embed">
                      <svg xmlns="http://www.w3.org/2000/svg" width="100%" height="100%" viewBox="0 0 20 20" fill="none">
                        <path d="M10.8567 1.66797C11.7946 1.66952 12.2698 1.67448 12.6805 1.6867L12.8422 1.69199C13.0291 1.69864 13.2134 1.70697 13.4357 1.71739C14.3224 1.75835 14.9273 1.89864 15.4586 2.10489C16.0078 2.31669 16.4717 2.6028 16.9349 3.06599C17.3974 3.52919 17.6836 3.99447 17.8961 4.54239C18.1016 5.07294 18.2419 5.67849 18.2836 6.5653C18.2935 6.78752 18.3015 6.97185 18.3081 7.15873L18.3133 7.32046C18.3255 7.73109 18.3311 8.2064 18.3328 9.14424L18.3335 9.76565C18.3336 9.84157 18.3336 9.9199 18.3336 10.0007L18.3335 10.2358L18.333 10.8572C18.3314 11.7951 18.3265 12.2703 18.3142 12.681L18.3089 12.8427C18.3023 13.0296 18.294 13.2139 18.2836 13.4362C18.2426 14.3229 18.1016 14.9278 17.8961 15.4591C17.6842 16.0083 17.3974 16.4722 16.9349 16.9354C16.4717 17.3979 16.0057 17.6841 15.4586 17.8966C14.9273 18.1021 14.3224 18.2424 13.4357 18.2841C13.2134 18.294 13.0291 18.302 12.8422 18.3086L12.6805 18.3138C12.2698 18.326 11.7946 18.3316 10.8567 18.3333L10.2353 18.334C10.1594 18.3341 10.0811 18.3341 10.0002 18.3341L9.76516 18.334L9.14375 18.3335C8.20591 18.3319 7.7306 18.327 7.31997 18.3147L7.15824 18.3094C6.97136 18.3028 6.78703 18.2945 6.56481 18.2841C5.67801 18.2431 5.07384 18.1021 4.5419 17.8966C3.99328 17.6847 3.5287 17.3979 3.06551 16.9354C2.60231 16.4722 2.3169 16.0062 2.1044 15.4591C1.89815 14.9278 1.75856 14.3229 1.7169 13.4362C1.707 13.2139 1.69892 13.0296 1.69238 12.8427L1.68714 12.681C1.67495 12.2703 1.66939 11.7951 1.66759 10.8572L1.66748 9.14424C1.66903 8.2064 1.67399 7.73109 1.68621 7.32046L1.69151 7.15873C1.69815 6.97185 1.70648 6.78752 1.7169 6.5653C1.75786 5.6778 1.89815 5.07364 2.1044 4.54239C2.3162 3.99377 2.60231 3.52919 3.06551 3.06599C3.5287 2.6028 3.99398 2.31739 4.5419 2.10489C5.07315 1.89864 5.67731 1.75905 6.56481 1.71739C6.78703 1.70749 6.97136 1.69941 7.15824 1.69287L7.31997 1.68763C7.7306 1.67544 8.20591 1.66988 9.14375 1.66808L10.8567 1.66797ZM10.0002 5.83405C7.69781 5.83405 5.83356 7.70033 5.83356 10.0007C5.83356 12.3032 7.69984 14.1674 10.0002 14.1674C12.3027 14.1674 14.1669 12.3011 14.1669 10.0007C14.1669 7.6983 12.3006 5.83405 10.0002 5.83405ZM10.0002 7.50072C11.381 7.50072 12.5002 8.61957 12.5002 10.0007C12.5002 11.3815 11.3813 12.5007 10.0002 12.5007C8.6195 12.5007 7.50023 11.3818 7.50023 10.0007C7.50023 8.61999 8.61908 7.50072 10.0002 7.50072ZM14.3752 4.58405C13.8008 4.58405 13.3336 5.05064 13.3336 5.62501C13.3336 6.19938 13.8002 6.66669 14.3752 6.66669C14.9496 6.66669 15.4169 6.2001 15.4169 5.62501C15.4169 5.05064 14.9488 4.58333 14.3752 4.58405Z" fill="currentColor" />
                      </svg>
                    </div>
                  </a>
                  <a
                    href="https://x.com/timmers_bv"
                    className="icon-button-outline-large w-inline-block"
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label="Volg ons op X"
                  >
                    <div className="icon-regular w-embed">
                      <svg xmlns="http://www.w3.org/2000/svg" width="100%" height="100%" viewBox="0 0 20 20" fill="none">
                        <path d="M14.3134 3.33398H16.6135L11.5884 8.98185L17.5 16.6673H12.8713L9.24592 12.0061L5.0977 16.6673H2.7962L8.17099 10.6263L2.5 3.33398H7.24622L10.5232 7.5945L14.3134 3.33398ZM13.5061 15.3135H14.7806L6.55369 4.61672H5.186L13.5061 15.3135Z" fill="currentColor" />
                      </svg>
                    </div>
                  </a>
                  <a
                    href="https://nl.linkedin.com/company/timmersbv"
                    className="icon-button-outline-large w-inline-block"
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label="Volg ons op LinkedIn"
                  >
                    <div className="icon-regular w-embed">
                      <svg xmlns="http://www.w3.org/2000/svg" width="100%" height="100%" viewBox="0 0 20 20" fill="none">
                        <path d="M15.2802 15.2825H13.059V11.8018C13.059 10.9718 13.0421 9.90375 11.9015 9.90375C10.7434 9.90375 10.5665 10.8069 10.5665 11.7406V15.2825H8.3452V8.125H10.479V9.10058H10.5077C10.8059 8.53808 11.5309 7.94437 12.614 7.94437C14.8646 7.94437 15.2809 9.42567 15.2809 11.3538L15.2802 15.2825ZM5.83648 7.14562C5.12148 7.14562 4.5471 6.56687 4.5471 5.855C4.5471 5.14375 5.1221 4.56563 5.83648 4.56563C6.54898 4.56563 7.12648 5.14375 7.12648 5.855C7.12648 6.56687 6.54835 7.14562 5.83648 7.14562ZM6.95023 15.2825H4.72273V8.125H6.95023V15.2825ZM16.3915 2.5H3.60773C2.99585 2.5 2.50085 2.98375 2.50085 3.58063V16.4194C2.50085 17.0168 2.99585 17.5 3.60773 17.5H16.3896C17.0009 17.5 17.5009 17.0168 17.5009 16.4194V3.58063C17.5009 2.98375 17.0009 2.5 16.3896 2.5H16.3915Z" fill="currentColor" />
                      </svg>
                    </div>
                  </a>
                  <a
                    href="https://www.facebook.com/TIMMERS.BV/"
                    className="icon-button-outline-large w-inline-block"
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label="Volg ons op Facebook"
                  >
                    <div className="icon-regular w-embed">
                      <svg xmlns="http://www.w3.org/2000/svg" width="100%" height="100%" viewBox="0 0 24 24" fill="none">
                        <path d="M12.001 2C6.47813 2 2.00098 6.47715 2.00098 12C2.00098 16.9913 5.65783 21.1283 10.4385 21.8785V14.8906H7.89941V12H10.4385V9.79688C10.4385 7.29063 11.9314 5.90625 14.2156 5.90625C15.3097 5.90625 16.4541 6.10156 16.4541 6.10156V8.5625H15.1931C13.9509 8.5625 13.5635 9.33334 13.5635 10.1242V12H16.3369L15.8936 14.8906H13.5635V21.8785C18.3441 21.1283 22.001 16.9913 22.001 12C22.001 6.47715 17.5238 2 12.001 2Z" fill="currentColor" />
                      </svg>
                    </div>
                  </a>
                </div>
              </div>

              <div className="w-layout-grid footer-13-links">
                <div className="column-large-2">
                  <div className="overline-small">Klantenservice</div>
                  <div className="column-regular-3">
                    <Link to="/contact" className="footer-13-link w-inline-block">
                      <div>Contact opnemen</div>
                    </Link>
                    <Link to="/contact" className="footer-13-link w-inline-block">
                      <div>Klacht melden</div>
                    </Link>
                    <Link to="/account-aanvragen" className="footer-13-link w-inline-block">
                      <div>Account aanvragen</div>
                    </Link>
                  </div>
                </div>


                <div id="w-node-_61589ee7-1221-1772-d9ba-1a4eeb0834eb-eb0834cc" className="column-large-2">
                  <div className="overline-small">Aanmelden voor onze nieuwsbrief</div>
                  <div className="footer-13-newsletter w-form">
                    <form
                      onSubmit={handleNewsletterSubmit}
                      id="mc-embedded-subscribe-form"
                      name="mc-embedded-subscribe-form"
                      className="footer-13-newsletter-form validate"
                      noValidate
                    >
                      <div className="footer-13-newsletter-row">
                        <input
                          className="form-input"
                          style={{ width: '250%' }}
                          maxLength="256"
                          name="EMAIL"
                          id="mce-EMAIL"
                          placeholder="E-mailadres"
                          type="email"
                          required
                        />
                        <div aria-hidden="true" style={{ position: 'absolute', left: '-5000px' }}>
                          <input type="text" name="b_4e1ead804e072658f96c27429_0f0992dddb" tabIndex="-1" defaultValue="" />
                        </div>
                        <input
                          type="submit"
                          name="subscribe"
                          id="mc-embedded-subscribe"
                          className="button w-button"
                          value={newsletterStatus === 'sending' ? 'Even geduld...' : 'Aanmelden'}
                          disabled={newsletterStatus === 'sending'}
                        />
                      </div>
                    </form>

                    {newsletterStatus === 'success' && (
                      <div className="form-success-message w-form-done" style={{ display: 'block', marginTop: '10px' }}>
                        <div>{newsletterMessage}</div>
                      </div>
                    )}

                    {newsletterStatus === 'error' && (
                      <div className="form-error-message w-form-fail" style={{ display: 'block', marginTop: '10px' }}>
                        <div>{newsletterMessage}</div>
                      </div>
                    )}
                  </div>
                </div>

                <div id="w-node-_61589ee7-1221-1772-d9ba-1a4eeb0834f9-eb0834cc" className="column-large-2">
                  <div className="overline-small">Navigatie</div>
                  <div className="w-layout-grid grid">
                    <div className="column-regular-3">
                      <Link to="/webshop/category/douche-bad" className="footer-13-link w-inline-block" reloadDocument>
                        <div>Douche & Bad</div>
                      </Link>
                      <Link to="/webshop/category/interieurbeglazing" className="footer-13-link w-inline-block" reloadDocument>
                        <div>Interieurbeglazing</div>
                      </Link>
                      <Link to="/webshop/category/vitrine-meubelbeslag" className="footer-13-link w-inline-block" reloadDocument>
                        <div>Vitrine- en meubelbeslag</div>
                      </Link>
                    </div>
                    <div className="column-regular-3">
                      <Link to="/webshop/category/balustrade" className="footer-13-link w-inline-block" reloadDocument>
                        <div>Balustrade</div>
                      </Link>
                      <Link to="/webshop/category/glasartikelen" className="footer-13-link w-inline-block" reloadDocument>
                        <div>Glasartikelen</div>
                      </Link>
                      <Link to="/webshop/category/glasoplossingen" className="footer-13-link w-inline-block" reloadDocument>
                        <div>Glasoplossingen</div>
                      </Link>
                    </div>
                    <div className="column-regular-3">
                      <Link to="/webshop/category/ventilatieroosters" className="footer-13-link w-inline-block" reloadDocument>
                        <div>Ventilatieroosters</div>
                      </Link>
                      <Link to="/downloads" className="footer-13-link w-inline-block" reloadDocument>
                        <div>Downloads</div>
                      </Link>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div className="footer-13-legal-section">
          <div className="container">
            <div className="footer-13-legal-row">
              <div className="footer-13-legal-links">
                <div className="paragraph-x-small text-color-tertiary">
                  ©{new Date().getFullYear()} Timmers B.V. - All Rights Reserved.
                </div>
                <div className="footer-13-divider"></div>
                <div className="paragraph-x-small text-color-tertiary">
                  Powered by Baka Glass
                </div>
              </div>
              <div className="footer-13-legal-links">
                <Link to="/privacy" className="footer-13-legal-link w-inline-block">
                  <div>Privacy Policy</div>
                </Link>
                <div className="footer-13-divider"></div>
                <a
                  href="/media/wysiwyg/Timmers_downloads/Timmers_Algemene_voorwaarden.pdf"
                  className="footer-13-legal-link w-inline-block"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <div>Algemene voorwaarden</div>
                </a>
                <div className="footer-13-divider"></div>
                <Link to="/retourneren" className="footer-13-legal-link w-inline-block">
                  <div>Retourneren</div>
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>
      <div className="mobile-block"></div>
    </footer>
  );
};

export default Footer;
